import { defineStore } from 'pinia';
import type { ProductProviderKey } from '#shared/types/goliath/goliath.types';

export type ReductionMethod =
  | 'RS' // Discount amount, single unit (Voucher)
  | 'RT' // Discount amount, single unit (Coupon)
  | 'RP' // Percent
  | 'PS'; // New price, single unit

export type BaseCoupon = {
  description?: string;
  reductionMethodCode?: ReductionMethod;
  status: 'empty' | 'VALID' | 'PARTIALREDEEMED' | 'brandError' | 'INVALID' | 'EXPIRED';
  productNumbersWhitelist?: string[];
  productNumbersBlacklist?: string[];
  providersWhitelist?: ('EGSolo_Brodos' | 'EGSolo_Telekom')[];
  providersBlacklist?: ('EGSolo_Brodos' | 'EGSolo_Telekom')[];
  reductionValue?: number;
  type?: 'coupon' | 'voucher';
  typeCode?: '01' | '02'; // 01: apply-able on 1 product;
  initialValue?: number;
};

export interface Coupon extends BaseCoupon {
  couponCode: string;
  expiryDate?: string;
}

export interface RedeemedCouponResponse extends BaseCoupon {
  roundingMethodCode: string;
  multiple: string;
}

export interface CouponState {
  coupon: Coupon | undefined;
}

export const useCouponStore = defineStore('coupon', {
  state: (): CouponState => ({
    coupon: undefined,
  }),

  getters: {
    getCoupon(state): Coupon | undefined {
      return state.coupon;
    },
    couponInfo(state): Coupon | undefined {
      return state.coupon;
    },
    compatibleArticleNumbers(state): string[] {
      return state.coupon?.productNumbersWhitelist || [];
    },
    incompatibleArticleNumbers(state): string[] {
      return state.coupon?.productNumbersBlacklist || [];
    },
    compatibleProductProviders(state): ProductProviderKey[] {
      if (!state.coupon) return [];
      if (
        state.coupon.providersWhitelist?.includes('EGSolo_Brodos') &&
        state.coupon.providersWhitelist?.includes('EGSolo_Telekom')
      ) {
        return [];
      }
      if (
        state.coupon.providersWhitelist?.includes('EGSolo_Telekom') ||
        state.coupon.providersBlacklist?.includes('EGSolo_Brodos')
      ) {
        return ['TELEKOM' as ProductProviderKey];
      }
      if (
        state.coupon.providersWhitelist?.includes('EGSolo_Brodos') ||
        state.coupon.providersBlacklist?.includes('EGSolo_Telekom')
      ) {
        return ['BRODOS' as ProductProviderKey];
      }
      return [];
    },
    isValidCoupon(state): boolean {
      return ['valid', 'partialredeemed'].includes(state.coupon?.status?.toLowerCase() || '');
    },
    errorStatus(state): string {
      return !this.isValidCoupon ? state.coupon?.status?.toLowerCase() || '' : '';
    },
    reductionValue(state): string | number | undefined {
      if (!state.coupon) return undefined;
      return state.coupon.reductionValue && state.coupon.reductionValue % 1 !== 0
        ? state.coupon.reductionValue.toFixed(2)
        : state.coupon.reductionValue;
    },
  },

  actions: {
    setCoupon(coupon: Coupon | undefined) {
      this.coupon = coupon;
    },
    setError(errorType: BaseCoupon['status']) {
      if (this.coupon) {
        this.coupon.status = errorType;
      }
    },

    async initializeCoupon(redeemedCoupon: string) {
      if (redeemedCoupon && redeemedCoupon !== this.coupon?.couponCode) {
        const data = await useShopApi().getCouponInfo(redeemedCoupon);
        this.setCoupon({
          ...data.couponVoucher,
          couponCode: redeemedCoupon,
        });
      }
    },

    async updateCoupon(couponCode: string) {
      const data = await useShopApi().getCouponInfo(couponCode);
      this.setCoupon({
        ...data.couponVoucher,
        couponCode: ['valid', 'partialredeemed'].includes(data.couponVoucher.status.toLowerCase())
          ? couponCode
          : '',
      });
    },

    async clearCoupon() {
      this.coupon = undefined;
    },

    reportBrandError() {
      this.setError('brandError');
    },
  },
});
